import Table from "components/Table";
import Pagination from "pages/data-table-view/components/Pagination";
import Toast from "pages/data-table-view/components/Toast";
import { useCallback, useEffect, useState } from "react";
import useTableSort from "hooks/useTableSort";
import useHttp from "hooks/useHttp";
import useTableFilters from "hooks/useTableFilters";
import SalesFilters from "./SalesFilters";
import SalesForm from "./SalesForm";
import SalesDetailsDrawer from "./SalesDetailsDrawer";
import SalesExportButton from "./SalesExportButton";
import { createSale, fetchSales, fetchAllSales } from "services/salesService";
import { formatBusinessDate } from "utils/businessDate";
import SalesSummaryCards from "./SalesSummaryCards";
import SaleCommissionAssignment, {
  formatCommissionDisplay,
} from "./SaleCommissionAssignment";
import { exportSalesToCsv, exportSalesToExcel } from "../utils/salesExportUtils";

const SESSION_KEY = "sales_filters";
const SEARCH_SESSION_KEY = "sales_search";

const DEFAULT_FILTERS = {
  dateRangeType: "thisMonth",
  customDateRange: null,
  status: undefined,
  saleType: undefined,
  source: undefined,
};

const DEFAULT_SEARCH = {
  productName: "",
  stripeCustomerId: "",
  stripePaymentIntentId: "",
  utmSource: "",
};

const EMPTY_FORM = {
  saleDate: "",
  productName: "",
  qty: "",
  unitPrice: "",
  totalAmount: "0.00",
  notes: "",
};

const formatCurrency = (value) =>
  `$${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatLabel = (value) =>
  value
    ? value
        .split("_")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ")
    : "-";

const STATUS_STYLES = {
  completed: "bg-green-100 text-green-800",
  refunded: "bg-orange-100 text-orange-800",
  partially_refunded: "bg-yellow-100 text-yellow-800",
  failed: "bg-red-100 text-red-800",
};

const StatusBadge = ({ status }) => (
  <span
    className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
      STATUS_STYLES[status] || "bg-gray-100 text-gray-800"
    }`}
  >
    {formatLabel(status)}
  </span>
);

export default function SalesTable() {
  const api = useHttp();
  const [search, setSearch] = useState(DEFAULT_SEARCH);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const { filters, setFilters, hydrated } = useTableFilters(SESSION_KEY, DEFAULT_FILTERS);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [selectedSaleId, setSelectedSaleId] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [savingCommissionId, setSavingCommissionId] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [summaryRefreshKey, setSummaryRefreshKey] = useState(0);

  const { sortConfig, requestSort } = useTableSort({
    initialKey: "saleDate",
    initialDirection: "descending",
    dateKeyMap: { createdAt: "createdAt" },
  });

  const fetchSalesData = async () => {
    setIsLoading(true);
    try {
      const result = await fetchSales(api, {
        page: currentPage,
        limit: itemsPerPage,
        search,
        filters,
      });
      setData(result.records || []);
      setTotalRecords(result.count || 0);
      setError(null);
    } catch {
      setError("Failed to fetch data. Please try again.");
      setToast({ show: true, message: "Failed to fetch data.", type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const savedSearch = sessionStorage.getItem(SEARCH_SESSION_KEY);
    if (savedSearch) {
      setSearch({ ...DEFAULT_SEARCH, ...JSON.parse(savedSearch) });
    }
  }, []);

  useEffect(() => {
    sessionStorage.setItem(SEARCH_SESSION_KEY, JSON.stringify(search));
  }, [search]);

  useEffect(() => {
    if (!hydrated) return;
    fetchSalesData();
  }, [currentPage, itemsPerPage, search, filters, hydrated]);

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleSearchChange = (newSearch) => {
    setSearch(newSearch);
    setCurrentPage(1);
  };

  const handleCreateClick = () => {
    setFormData(EMPTY_FORM);
    setIsFormOpen(true);
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setFormData(EMPTY_FORM);
  };

  const handleFormChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleFormSubmit = async () => {
    try {
      await createSale(api, {
        saleDate: formData.saleDate,
        productName: formData.productName.trim(),
        qty: Number(formData.qty),
        unitPrice: Number(formData.unitPrice),
        totalAmount: Number(formData.totalAmount),
        notes: formData.notes?.trim() || undefined,
      });
      setToast({ show: true, message: "Manual sale created.", type: "success" });
      handleFormClose();
      setSummaryRefreshKey((k) => k + 1);
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        fetchSalesData();
      }
    } catch (err) {
      const message =
        err?.response?.data?.message || "Failed to create manual sale.";
      setToast({ show: true, message, type: "error" });
    }
  };

  const handleRowClick = (item) => {
    setSelectedSaleId(item._id);
    setIsDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    setSelectedSaleId(null);
  };

  const handleExport = async (format) => {
    setIsExporting(true);
    try {
      const rows = await fetchAllSales(api, { search, filters });
      if (rows.length === 0) {
        setToast({ show: true, message: "No sales to export.", type: "error" });
        return;
      }
      if (format === "csv") {
        exportSalesToCsv(rows);
      } else {
        await exportSalesToExcel(rows);
      }
      setToast({ show: true, message: "Export completed.", type: "success" });
    } catch {
      setToast({ show: true, message: "Failed to export sales.", type: "error" });
    } finally {
      setIsExporting(false);
    }
  };

  const handleSort = (key) => {
    requestSort(key, data, setData, columns);
  };

  const handleSaleUpdated = (saleId, fields) => {
    setData((prev) =>
      prev.map((row) => (row._id === saleId ? { ...row, ...fields } : row))
    );
  };

  const handleCommissionSavingChange = (saleId, saving) => {
    setSavingCommissionId(saving ? saleId : null);
  };

  const showToast = ({ message, type }) =>
    setToast({ show: true, message, type });

  const columns = [
    {
      key: "number",
      label: "No.",
      render: (item, idx) => {
        const page = Number(currentPage) || 1;
        const perPage = Number(itemsPerPage) || 10;
        const rowIdx = typeof idx === "number" ? idx : data.findIndex((d) => d._id === item._id);
        return (page - 1) * perPage + rowIdx + 1;
      },
    },
    {
      key: "saleDate",
      label: "Sale Date",
      sortable: true,
      sortValue: (item) => (item.saleDate ? new Date(item.saleDate).getTime() : 0),
      render: (item) => (item.saleDate ? formatBusinessDate(item.saleDate) : "-"),
    },
    { key: "productName", label: "Product Name", sortable: true },
    {
      key: "qty",
      label: "Qty",
      sortable: true,
      sortValue: (item) => Number(item.qty) || 0,
      className: "text-right",
      headerClassName: "text-right",
      heaserTextAlign: "justify-end",
    },
    {
      key: "unitPrice",
      label: "Unit Price",
      sortable: true,
      render: (item) => formatCurrency(item.unitPrice),
      sortValue: (item) => Number(item.unitPrice) || 0,
      className: "text-right",
      headerClassName: "text-right",
      heaserTextAlign: "justify-end",
    },
    {
      key: "totalAmount",
      label: "Total Amount",
      sortable: true,
      render: (item) => formatCurrency(item.totalAmount),
      sortValue: (item) => Number(item.totalAmount) || 0,
      className: "text-right",
      headerClassName: "text-right",
      heaserTextAlign: "justify-end",
    },
    {
      key: "refundAmount",
      label: "Refund Amount",
      sortable: true,
      render: (item) => formatCurrency(item.refundAmount),
      sortValue: (item) => Number(item.refundAmount) || 0,
      className: "text-right",
      headerClassName: "text-right",
      heaserTextAlign: "justify-end",
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      render: (item) => <StatusBadge status={item.status} />,
    },
    {
      key: "saleType",
      label: "Sale Type",
      sortable: true,
      render: (item) => formatLabel(item.saleType),
    },
    {
      key: "source",
      label: "Source",
      sortable: true,
      render: (item) => formatLabel(item.source),
    },
    {
      key: "utm_source",
      label: "UTM Source",
      sortable: true,
      sortValue: (item) => (item.utm_source || "").toLowerCase(),
      render: (item) => item.utm_source || "-",
    },
    {
      key: "commissionDetails",
      label: "Commission Details",
      className: "whitespace-normal",
      render: (item) => (
        <SaleCommissionAssignment
          sale={item}
          api={api}
          onUpdated={handleSaleUpdated}
          onSavingChange={handleCommissionSavingChange}
          onToast={showToast}
        />
      ),
    },
    {
      key: "commission",
      label: "Commission",
      className: "whitespace-normal",
      render: (item) =>
        formatCommissionDisplay(item, savingCommissionId === item._id),
    },
    {
      key: "createdAt",
      label: "Created At",
      sortable: true,
      sortValue: (item) => (item.createdAt ? new Date(item.createdAt).getTime() : 0),
      render: (item) =>
        item.createdAt
          ? new Date(item.createdAt).toLocaleString("en-US", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
              hour12: true,
            })
          : "-",
    },
  ];

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(totalRecords / itemsPerPage) || 1;
  const indexOfFirstItem = (currentPage - 1) * itemsPerPage;
  const indexOfLastItem = Math.min(indexOfFirstItem + itemsPerPage, totalRecords);

  return (
    <div className="bg-background">
      <SalesSummaryCards
        api={api}
        filters={filters}
        refreshKey={summaryRefreshKey}
      />

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <SalesFilters
            filters={filters}
            search={search}
            onFiltersChange={handleFiltersChange}
            onSearchChange={handleSearchChange}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={handleItemsPerPageChange}
            addButton={
              <button
                type="button"
                onClick={handleCreateClick}
                className="bg-primary-600 text-white px-3 py-1.5 rounded hover:bg-primary-700 text-sm flex-shrink-0 h-8"
              >
                Add Manual Sale
              </button>
            }
            exportButton={
              <SalesExportButton
                onExport={handleExport}
                isExporting={isExporting}
                disabled={totalRecords === 0 || isLoading}
              />
            }
          />
        </div>

        <Table
          columns={columns}
          data={data}
          isLoading={isLoading}
          error={error}
          sortConfig={sortConfig}
          onSort={handleSort}
          onRetry={fetchSalesData}
          onRowClick={handleRowClick}
          emptyMessage="No sales found. Try adjusting your search or filters."
        />

        {!isLoading && !error && data.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            totalItems={totalRecords}
            itemsPerPage={itemsPerPage}
            showingFrom={indexOfFirstItem + 1}
            showingTo={indexOfLastItem}
          />
        )}
      </div>

      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}

      <SalesDetailsDrawer
        open={isDrawerOpen}
        saleId={selectedSaleId}
        api={api}
        onClose={handleDrawerClose}
        onUpdated={handleSaleUpdated}
      />

      <SalesForm
        isOpen={isFormOpen}
        onClose={handleFormClose}
        onSubmit={handleFormSubmit}
        editingSales={null}
        initialData={formData}
        onFormChange={handleFormChange}
      />
    </div>
  );
}
